import { useState, useEffect } from "react";
import {
    SpaceBetween,
    Header,
    Button,
    Container,
    StatusIndicator,
    FormField,
    Input,
    Select,
    Tiles,
    Checkbox,
    SegmentedControl,
    Cards,
    Badge,
    Grid,
    Box
} from "@cloudscape-design/components";
import { useNotification } from "contexts/NotificationContext";
import { organizationService } from "services/organizationService";
import { getCurrencySymbol, makeFirstCharacterUppercase, openInNewTab } from "utils/Utils";
import AppConfig from "configs/AppConfig";
import NEW_PLANS from "configs/newPlans.json";
import SettingsService from "services/SettingsService";
import { RegionOption, SelectOptionType } from "types/types";
import { DomainProp } from "types/organizationTypes";
import { ClusterDetailsStepProp } from "types/invoiceTypes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTag } from "@fortawesome/free-solid-svg-icons";
import { clusterSizes, securityAddOnsList } from "configs/settings";
import OrderSummaryCard from "./OrderSummaryCard";
import { freeTrialPeriodDays } from "configs/settings";





const ClusterDetailsStep = ({
    useCustomDomain, 
    setUseCustomDomain,
    deploymentData, 
    setDeploymentData, 
    organizationError, 
    organizationOptions, 
    hasUsedFreeTrial, 
    loadingOrganizations,
    securityAddOns,
    setSecurityAddOns,
    orderSummary,
    selectedPlan,
    selectedSize
}:ClusterDetailsStepProp) => {

    const { addNotification } = useNotification();    
    
    // Regions
    const [regionOptions, setRegionOptions] = useState<RegionOption[]>([]);
    const [loadingRegionOptions, setLoadingRegionOptions] = useState<boolean>(false);
    const [regionOptionsError, setRegionOptionsError] = useState<string | null>(null);
    
    // Domains
    const [domainsList, setDomainsList] = useState<SelectOptionType[]>([]);
    const [loadingDomains, setLoadingDomains] = useState<boolean>(false);
    const [domainError, setDomainError] = useState<string | null>(null);
    
    // Keycloak Versions
    const [kcVersions, setKcVersions] = useState<SelectOptionType[]>([]);
    const [loadingKcVersions, setLoadingKcVersions] = useState<boolean>(false);
    const [kcVersionsError, setKcVersionsError] = useState<string | null>(null);


    const securityList = securityAddOnsList.map(item => ({
        ...item,
        disabled: !selectedPlan.supportedSecurityAddOns.includes(item.id)
    }));


    const isSecurityAddOnsSupported = selectedPlan?.supportedSecurityAddOns?.length > 0;
    const isCustomDomainSupported = selectedPlan.isCustomDomainSupported;


    const isYearlySelected = deploymentData.billingCycle === "yearly";


    // Fetch Domains
    const fetchDomains = async () => {
        setDomainsList([]);
        const id = deploymentData.organizationId?.value ?? "";
        if (!id) return;

        try {
            setLoadingDomains(true);
            setDomainError(null);
            const domains = await organizationService.fetchDomains(id);
        
            const modifiedDomains = domains
            .map((d: DomainProp) => ({
                label: d.name,
                value: d.hosted_zone_id,
                disabled: !d.is_verified || d.is_used, // disable if is not verified or in use
                disabledReason: !d.is_verified ? "Unverified domain" : d.is_used ? "In use by another deployment!" : ""
            }));

            setDomainsList(modifiedDomains);
        } catch (error) {
            console.error("Error fetching domains:", error);
            setDomainError("Failed to fetch domains. Please try again later.");
            setDomainsList([]);
            addNotification("error", "Failed to fetch domains");
        } finally {
            setLoadingDomains(false);
        }
    };

    // Fetch Regions
    const fetchRegions = async ()=>{
        try {
            setLoadingRegionOptions(true);
            setRegionOptionsError(null);
            const { regions } = await SettingsService.getDedicatedClusterRegionOptions();
            setRegionOptions(regions);
        } catch (error) {
            setRegionOptionsError("Failed to load regions. Please try again later.")
            addNotification("error", "Failed to load regions. Please try again later.")
            console.error("Failed to load regions. Please try again later.", error);
        } finally{
            setLoadingRegionOptions(false);
        }
    }

    // Fetch Keyclock Versions
    const fetchKeycloakVersions = async ()=>{
        try {
            setLoadingKcVersions(true);
            setKcVersionsError(null);
            const data = await SettingsService.getKeycloakVersions();

            const formedKcVersions = data.kcVersions.map((v)=>{
                return {
                    label: `${v.value}${v.isLatest ? " (Latest)" : ""}`,
                    value: v.value,
                    iconSvg: <FontAwesomeIcon icon={faTag} />
                }
            });
            
            setKcVersions(formedKcVersions);
            
            // Set kc version to the default one
            if(data.kcVersions.length !== 0 && !deploymentData.keycloakVersion) setDeploymentData({...deploymentData, keycloakVersion: formedKcVersions.find((v)=>v.value === data.default.value) ?? null});
            
        } catch (error) {
            setKcVersionsError("Failed to load keycloak versions. Please try again later.")
            addNotification("error", "Failed to load keycloak versions. Please try again later.")
            console.error("Failed to load keycloak versions. Please try again later.", error);
        } finally{
            setLoadingKcVersions(false);
        }
    }

    // Reset Domain Field
    const handleResetDomainField = (customDomainIsChecked:boolean)=>{

        setDeploymentData(prev => ({
            ...prev,
            domainName: "",
            hostedZoneId: `${customDomainIsChecked ? "" : AppConfig.DEFAULT_HOSTED_ZONE_ID}`,
        }));

        if(customDomainIsChecked) fetchDomains();
        else{
            setDomainError(null);
            setDomainsList([]);
        }
    }


    
    useEffect(() => {
        fetchKeycloakVersions();
        fetchRegions();
    }, []);

    // Fetch domains on initial mount and whenever `useCustomDomain` changes
    useEffect(()=>{
        if(useCustomDomain) fetchDomains();
    },[useCustomDomain])



    // Domain Field UI Section
    const renderDomainSection = () => {
        if(useCustomDomain) {
            return (
                <div className="inline-space">
                    <div className="flex-1">
                        <Select
                            options={domainsList}
                            selectedOption={deploymentData.domainName ? {label: deploymentData.domainName, value: deploymentData.hostedZoneId} : null}
                            placeholder={loadingDomains ? "Loading Domains..." :  "Select custom domain"}
                            statusType={loadingDomains ? "loading" : domainError ? "error" : "finished"}
                            loadingText="Loading domains"
                            errorText={domainError ?? undefined}
                            empty="No custom domains found"
                            onChange={({ detail }) =>{
                                setDeploymentData((prev) => ({
                                    ...prev,
                                    domainName: detail.selectedOption.label ?? "",
                                    hostedZoneId: detail.selectedOption.value ?? "",
                                    errors: { ...prev.errors, domainName: undefined },
                                }))
                            }}
                        />
                    </div>

                    <SpaceBetween size="xs" direction="horizontal">
                        <Button iconName="refresh" onClick={fetchDomains} />
                        <Button iconName="add-plus" onClick={()=>{
                            const id = deploymentData.organizationId?.value;
                            if(id) openInNewTab(`/organizations/${id}/domains`);
                        }} />
                    </SpaceBetween>
                </div>
            );
        } else {
            return (
                <div className="domain-input-container">
                    <Input
                        placeholder="your-domain"
                        value={deploymentData.domainName}
                        onChange={({ detail }) => {
                            const value = detail.value.trim();
                            const domainRegex = /^(?!-)[a-z0-9-]{1,50}$/;
                            let error: string | undefined;

                            if (!value) {
                                error = "Please enter a domain name.";
                            } else if (!domainRegex.test(value)) {
                                error =
                                    "Invalid domain name. Only lower case letters, digits, and hyphens allowed (no spaces, no special characters).";
                            } else if (value.length > 50) {
                                error = "The domain name has too many characters.";
                            }

                            setDeploymentData((prev) => ({
                                ...prev,
                                domainName: value,
                                errors: { ...prev.errors, domainName: error },
                            }));
                        }}
                        className="domain-input"
                    />

                    {/* Read-only suffix */}
                    <div className="suffix-box">
                        .authn-z.link
                    </div>
                </div>
            )
        }
    }


    return (
        <Grid
            gridDefinition={[
                { colspan: { default: 12, xs: 7, s:8, l: 8, xl: 9 } },
                { colspan: { default: 12, xs: 5, s:4, l: 4, xl: 3 } }
            ]}
        >
            <SpaceBetween size="xl">

                {/* Cluster type container */}
                <Container 
                    header={
                        <Header 
                            variant="h2" 
                            actions={
                                <SegmentedControl
                                    selectedId={deploymentData.billingCycle}
                                    onChange={({ detail }) =>
                                        setDeploymentData((prev) => ({
                                            ...prev,
                                            billingCycle: detail.selectedId,
                                            errors: { ...prev.errors, billingCycle: undefined },
                                        }))
                                    }
                                    options={[
                                        { text: "Monthly", id: "monthly" },
                                        { text: "Yearly -20%", id: "yearly" }
                                    ]}
                                />
                            }
                        >
                            Cluster Type
                        </Header>
                    }
                >
                    <br />
                    
                    <SpaceBetween size="l">
                        {/* Plans Section */}
                        <FormField errorText={deploymentData.errors.deploymentPlan} stretch>
                            <Tiles
                                className="cluster-plan-cards-wrapper"
                                onChange={({ detail }) => {
                                    setDeploymentData((prev) => ({
                                        ...prev,
                                        deploymentPlan: detail.value,
                                        errors: { ...prev.errors, deploymentPlan: undefined },
                                    }));
                                }}

                                value={deploymentData.deploymentPlan ?? ""}

                                items={NEW_PLANS.map((plan) => {
                                    
                                    const selectedSize = (deploymentData.clusterSize ?? "small") as keyof typeof plan.size;

                                    const selectedPlan = plan.size[selectedSize];

                                    const yearlyPrice  = (Math.ceil(selectedPlan.price.monthly * 12 * 0.8)); // discounted 80%
                                    
                                    const monthlyPriceFromYearly = Math.ceil(Number(yearlyPrice) / 12);

                                    const currency = getCurrencySymbol(selectedPlan.price.currency);

                                    return {
                                        label: (
                                            <div className="cluster-tiles-label">
                                                {plan.name}
                                            </div>
                                        ),
                                        value: plan.name.toLowerCase(),
                                        description: 
                                            !isYearlySelected 
                                                ? 
                                                    <div className="description-body">
                                            
                                                        <div className="per-month"> 
                                                            <span className="price"> {currency}{selectedPlan.price.monthly} </span> /month
                                                        </div>

                                                        <div>
                                                            Billed monthly
                                                        </div>

                                                        <div>{selectedPlan.MAU.description}</div>
                                                    </div>
                                                :
                                                    <div className="description-body">

                                                        <div className="per-month">
                                                            <span className="price">{currency}{monthlyPriceFromYearly}</span> /month
                                                        </div>

                                                        <div>
                                                            Billed at  <span className="annual-price">{currency}{yearlyPrice} </span> annually
                                                        </div>
                                                        
                                                        <div>{selectedPlan.MAU.description}</div>
                                                    </div>
                                    }
                                })}
                            />
                        </FormField>

                        {/* Cluster sizes Section */}
                        <FormField 
                            errorText={deploymentData.errors.clusterSize} 
                            stretch
                            label="Cluster Size"
                        >
                            <Tiles
                                className="cluster-size-cards-wrapper"
                                onChange={({ detail }) => {
                                    setDeploymentData((prev) => ({
                                        ...prev,
                                        clusterSize: detail.value,
                                        errors: { ...prev.errors, clusterSize: undefined },
                                    }));
                                }}

                                value={deploymentData.clusterSize ?? "small"} // default to "small"

                                items={clusterSizes.map((clusterSize) => {
                                    
                                    return {
                                        label: (
                                            <div className="cluster-tiles-label">
                                                {makeFirstCharacterUppercase(clusterSize)}
                                            </div>
                                        ),
                                        value: clusterSize
                                    }
                                })}
                            />
                        </FormField>
                    </SpaceBetween>
                </Container>

                {/* Cluster details container */}
                <Container header={<Header variant="h2">Cluster Details</Header>}>
                    <br />
                    <SpaceBetween size="l">

                        {!hasUsedFreeTrial && <StatusIndicator type="info">
                            Start your {freeTrialPeriodDays}-days free trial — Your dedicated cluster will be ready instantly.
                        </StatusIndicator>}
                        
                        {/* Regions Field */}
                        <FormField
                            stretch
                            label={<>Region <span className="required-field">*</span></>}
                            errorText={deploymentData.errors.region}
                        >
                            <Select
                                options={regionOptions}
                                selectedOption={deploymentData.region}
                                onChange={({ detail }) =>
                                    setDeploymentData((prev) => ({
                                        ...prev,
                                        region: detail.selectedOption,
                                        errors: { ...prev.errors, region: undefined },
                                    }))
                                }
                                placeholder="Select one region"
                                disabled={!!regionOptionsError}
                                statusType={
                                    loadingRegionOptions
                                        ? "loading"
                                        : regionOptionsError
                                            ? "error"
                                            : undefined
                                }
                            />
                        </FormField>

                        {/* Name Field */}
                        <FormField
                            stretch
                            label={<>Name <span className="required-field">*</span></>}
                            constraintText={<>
                                Name must be unique and has maximum 30 characters. Character count: {deploymentData.name.length}/30
                            </>}
                            errorText={
                                deploymentData.errors.name ||
                                (deploymentData.name.length > 30 &&
                                    "The name has too many characters.")
                            }
                        >
                            <Input
                                value={deploymentData.name}
                                placeholder="Enter cluster name"
                                onChange={({ detail }) =>{
                                    const value = detail.value.trim();
                                    const regex = /^(?![0-9])(?!-)(?!.*-$)[a-z0-9-]+$/;
                                    let error: string | undefined;
                                    const forbiddenPrefixes = ['internal-', 'aws-', 'amazon-'];
                                    
                                    if (!value) {
                                        error = "Please enter a name.";
                                    } else if (!regex.test(value)) {
                                        error = "Name must start with a letter, contain only letters, numbers or hyphens, and cannot end with a hyphen.";
                                    } else if (value.includes("--")){
                                        error = "Name cannot contain consecutive hyphens."
                                    } else if (forbiddenPrefixes.some(p => value.startsWith(p))) {
                                        error = "Name starts with a reserved prefix.";
                                    } else if (value.length > 30) {
                                        error = "Name has too many characters.";
                                    }
                                        
                                    setDeploymentData((prev) => ({
                                        ...prev,
                                        name: value,
                                        errors: { ...prev.errors, name: error },
                                    }));
                                }}
                            />
                        </FormField>

                        {/* Organization Field */}
                        <FormField
                            stretch
                            label={<>Organization <span className="required-field">*</span></>}
                            errorText={deploymentData.errors.organizationId || organizationError}
                        >
                            <Select
                                options={organizationOptions}
                                selectedOption={deploymentData.organizationId}
                                onChange={({ detail }) => {
                                    setDeploymentData((prev) => ({
                                        ...prev,
                                        organizationId: detail.selectedOption,
                                        errors: { ...prev.errors, organizationId: undefined },
                                    }));
                                }}

                                placeholder="Select one organization"
                                loadingText={loadingOrganizations ? "Loading organizations..." : undefined}
                                disabled={!!organizationError}
                                statusType={
                                    loadingOrganizations
                                        ? "loading"
                                        : organizationError
                                            ? "error"
                                            : undefined
                                }
                            />
                        </FormField>

                        {/* Domain Field */}
                        <FormField
                            stretch
                            label={
                                <div>
                                    Domain name <span className="required-field">*</span>
                                    <Checkbox
                                        disabled={!deploymentData.organizationId || !isCustomDomainSupported}
                                        onChange={({ detail }) =>{
                                            setUseCustomDomain(detail.checked);
                                            handleResetDomainField(detail.checked);
                                        }}
                                        checked={useCustomDomain}
                                    >
                                        Use custom domain <Badge className={isCustomDomainSupported ? "" : "container-disabled"}>Scale+</Badge>
                                    </Checkbox>

                                    {isCustomDomainSupported && !deploymentData.organizationId && (
                                        <Box variant="small" color="text-status-inactive">
                                            Select an organization to enable custom domains.
                                        </Box>
                                    )}
                                </div>
                            }
                            constraintText={!useCustomDomain &&
                                <>
                                    Domain name must be unique and has a maximum of 50 characters.
                                    Character count: {deploymentData.domainName?.length}/50
                                </>
                            }
                            errorText={deploymentData.errors.domainName}
                        >
                            {renderDomainSection()}
                            
                        </FormField>

                        {/* Keycloak Version Field */}
                        <FormField
                            stretch
                            label={<>Keycloak version <span className="required-field">*</span></>}
                            errorText={deploymentData.errors.keycloakVersion}
                        >
                            <Select
                                options={kcVersions}
                                selectedOption={deploymentData.keycloakVersion}
                                onChange={({ detail }) =>                                        
                                    setDeploymentData((prev) => ({
                                        ...prev,
                                        keycloakVersion: detail.selectedOption,
                                        errors: { ...prev.errors, keylcoakVersion: undefined },
                                    }))
                                }
                                placeholder="Select one keycloak version"
                                filteringPlaceholder="Search ..."
                                filteringType="auto"
                                empty="No keycloak versions found"
                                filteringClearAriaLabel="Clear"
                                noMatch="No matches found"
                                virtualScroll = {kcVersions.length > 5}
                                disabled={!!kcVersionsError}
                                triggerVariant="option"
                                statusType={
                                    loadingKcVersions
                                        ? "loading"
                                        : kcVersionsError
                                            ? "error"
                                            : undefined
                                }
                            />
                        </FormField>

                    </SpaceBetween>
                </Container>

                {/* Security add-ons container */}
                <div className={isSecurityAddOnsSupported ? "" : "container-disabled"}>
                    <Container 
                        header={
                            <Header 
                                variant="h2"
                                description="Security add-ons are available on Scale plan and above."
                                info={<Badge>Scale+</Badge>}
                            >
                                Security Add-ons
                            </Header>
                        }
                    >
                        <Cards
                            items={securityList}
                            isItemDisabled={item => item.disabled}
                            selectionType="multi"
                            entireCardClickable
                            selectedItems={securityList.filter(
                                item => !item.disabled && deploymentData[item.deploymentProperty as keyof typeof deploymentData]
                            )}
                            onSelectionChange={({ detail }) => {
                                const selectedIds = new Set(
                                    detail.selectedItems.map(item => item.id)
                                );

                                setSecurityAddOns(
                                    detail.selectedItems.map(item => item.id)
                                );

                                setDeploymentData(prev => {
                                    const updatedData = { ...prev };
                                    const securityData = updatedData as unknown as Record<string, boolean>;

                                    securityAddOnsList.forEach(item => {
                                    securityData[item.deploymentProperty] =
                                        selectedIds.has(item.id);
                                    });

                                    return updatedData;
                                });
                            }}
                            cardDefinition={{
                                header: item => (
                                    <div
                                        style={{
                                            opacity: item.disabled ? 0.5 : 1,
                                        }}
                                    >
                                        {item.name}
                                    </div>
                                ),
                                sections: [
                                    {
                                        id: "description",
                                        content: item => (
                                            <div
                                                style={{
                                                    opacity: item.disabled ? 0.5 : 1,
                                                }}
                                            >
                                                <Box variant="small"> {item.description}</Box> 
                                                {!isYearlySelected 
                                                    ? 
                                                        <div>
                                                            {getCurrencySymbol(item.currency)}{item.price} /month
                                                        </div> 
                                                    : 
                                                        <div>
                                                            {getCurrencySymbol(item.currency)}{Math.ceil(item.price * 12 * 0.8)} /year 
                                                        </div>
                                                            
                                                      
                                                }
                                            </div>
                                        ),
                                    },
                                ],
                            }}
                        />
                    </Container>
                </div>
            </SpaceBetween>

            {/* Order Summary section */}
            <div className="order-summary-column">
                <OrderSummaryCard
                    orderSummary={orderSummary}
                    currency={selectedPlan?.size[selectedSize].price?.currency ?? ""}
                    securityAddOns={securityAddOns}
                    hasUsedFreeTrial={hasUsedFreeTrial}
                    deploymentData={deploymentData}
                />   
            </div>
        </Grid>
    );
};

export default ClusterDetailsStep;